import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { FinancialAcccountActions } from './financial-account.actions';
import {
  FilterValues,
  FinancialAccountActivityItem,
  FinancialAccountDetails,
  FinancialAccountJournalActivityItem,
  FinancialAccountListItem,
  LimitItem,
} from '@shared/models';
export interface FinancialAccountState {
  financialAccounts: FinancialAccountListItem[];
  financialAccountsTotalElements: number;
  financialAccountsActiveFilters: FilterValues | null;
  financialAccountDetails: FinancialAccountDetails | null;
  isLoading: boolean;
  financialAccountLimits: LimitItem[] | null;
  financialAccountLimitsLoading: boolean;
  financialAccountActivity: FinancialAccountActivityItem[];
  financialAccountActivityTotalElements: number;
  financialAccountActivityActiveFilters: FilterValues | null;
  financialAccountActivityLoading: boolean;
  journalActivity: FinancialAccountJournalActivityItem[];
  journalActivityTotalElements: number;
  journalActivityActiveFilters: FilterValues | null;
  journalActivityLoading: boolean;
}

export const financialAccountInitialState: FinancialAccountState = {
  financialAccounts: [],
  financialAccountsTotalElements: 0,
  financialAccountsActiveFilters: null,
  financialAccountDetails: null,
  isLoading: false,
  financialAccountLimits: null,
  financialAccountLimitsLoading: false,
  financialAccountActivity: [],
  financialAccountActivityTotalElements: 0,
  financialAccountActivityActiveFilters: { date: 'DEFAULT' },
  financialAccountActivityLoading: false,
  journalActivity: [],
  journalActivityTotalElements: 0,
  journalActivityActiveFilters: { date: 'DEFAULT' },
  journalActivityLoading: false,
};

export const financialAccountFeature = createFeature({
  name: 'financialAccount',
  reducer: createReducer(
    financialAccountInitialState,
    on(
      FinancialAcccountActions.loadFinancialAccounts,
      (state): FinancialAccountState => ({ ...state, financialAccounts: [], financialAccountsTotalElements: 0, isLoading: true }),
    ),
    on(FinancialAcccountActions.loadFinancialAccountsSuccess, (state, { financialAccounts, totalElements }): FinancialAccountState => {
      return { ...state, financialAccounts, financialAccountsTotalElements: totalElements, isLoading: false };
    }),
    on(FinancialAcccountActions.loadFinancialAccountsFailure, (state): FinancialAccountState => {
      return { ...state, financialAccounts: [], financialAccountsTotalElements: 0, isLoading: false };
    }),
    on(
      FinancialAcccountActions.loadFinancialAccountDetails,
      (state): FinancialAccountState => ({
        ...state,
        financialAccountDetails: null,
      }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountDetailsSuccess,
      (state, { financialAccount }): FinancialAccountState => ({ ...state, financialAccountDetails: financialAccount }),
    ),
    on(
      FinancialAcccountActions.setFinancialAccountsActiveFilters,
      (state, { filters }): FinancialAccountState => ({ ...state, financialAccountsActiveFilters: filters }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountActivity,
      (state): FinancialAccountState => ({
        ...state,
        financialAccountActivity: [],
        financialAccountActivityTotalElements: 0,
        financialAccountActivityLoading: true,
      }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountActivitySuccess,
      (state, { items, totalElements }): FinancialAccountState => ({
        ...state,
        financialAccountActivity: items,
        financialAccountActivityTotalElements: totalElements,
        financialAccountActivityLoading: false,
      }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountActivityFailure,
      (state): FinancialAccountState => ({ ...state, financialAccountActivityLoading: false }),
    ),
    on(
      FinancialAcccountActions.setFinancialAccountsActivityActiveFilters,
      (state, { filters }): FinancialAccountState => ({ ...state, financialAccountActivityActiveFilters: filters }),
    ),
    on(
      FinancialAcccountActions.loadJournalActivity,
      (state): FinancialAccountState => ({
        ...state,
        journalActivity: [],
        journalActivityTotalElements: 0,
        journalActivityLoading: true,
      }),
    ),
    on(
      FinancialAcccountActions.loadJournalActivitySuccess,
      (state, { items, totalElements }): FinancialAccountState => ({
        ...state,
        journalActivity: items,
        journalActivityTotalElements: totalElements,
        journalActivityLoading: false,
      }),
    ),
    on(
      FinancialAcccountActions.loadJournalActivityFailure,
      (state): FinancialAccountState => ({ ...state, journalActivityLoading: false }),
    ),
    on(
      FinancialAcccountActions.setJournalActivityActiveFilters,
      (state, { filters }): FinancialAccountState => ({ ...state, journalActivityActiveFilters: filters }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountLimitsSuccess,
      (state, { financialAccountLimits }): FinancialAccountState => ({
        ...state,
        financialAccountLimits: financialAccountLimits,
        financialAccountLimitsLoading: false,
      }),
    ),
    on(
      FinancialAcccountActions.loadFinancialAccountLimitsFailure,
      (state): FinancialAccountState => ({ ...state, financialAccountLimitsLoading: false }),
    ),
  ),
  extraSelectors: ({
    selectFinancialAccountsActiveFilters,
    selectFinancialAccountActivityActiveFilters,
    selectJournalActivityActiveFilters,
  }) => ({
    selectFinancialAccountsFiltersRequest: createSelector(selectFinancialAccountsActiveFilters, (activeFilters) =>
      getActiveFilters(activeFilters),
    ),
    selectFinancialAccountsActiveFiltersCount: createSelector(selectFinancialAccountsActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
    selectFinancialAccountActivityFiltersRequest: createSelector(selectFinancialAccountActivityActiveFilters, (activeFilters) =>
      getActiveFilters(activeFilters),
    ),
    selectFinancialAccountActivityActiveFiltersCount: createSelector(selectFinancialAccountActivityActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
    selectJournalActivityFiltersRequest: createSelector(selectJournalActivityActiveFilters, (activeFilters) =>
      getActiveFilters(activeFilters),
    ),
    selectJournalActivityActiveFiltersCount: createSelector(selectJournalActivityActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
  }),
});

const getActiveFilters = (activeFilters: FilterValues | null) => {
  if (!activeFilters) {
    return;
  }
  const filters: Record<string, string> = {};
  Object.keys(activeFilters).map((filterKey) => {
    const value = activeFilters[filterKey];
    if (value && value !== 'all') {
      filters[filterKey] = value;
    }
  });
  return filters;
};

const getActiveFiltersCount = (activeFilters: FilterValues | null) => {
  if (!activeFilters) {
    return 0;
  }
  return Object.values(activeFilters).filter((value) => !!value && value !== 'all').length;
};
